import React, { useEffect, useState } from "react";
import { Box, FormControl, MenuItem, Select } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { COLORS, Loading } from "components";
import { useMutation } from "@tanstack/react-query";
import { updateVarianceComparisonConsumption, updateVarianceComparisonEmissions, updateVarianceComparisonFinancials } from "api";
import { sortedDates } from "utils";
import { chartColors, commonStyles } from "../styles";
import { VarianceChartProps } from "../types";

export const CombinedChart = ({
  chartTitle,
  subTabName,
  searchFilters,
  startDate,
  endDate,
  currentMainTab
}: VarianceChartProps) => {
  const [comparisonPeriod, setComparisonPeriod] = useState<string>("mom");
  const [varianceComparisonData, setVarianceComparisonData] = useState<any>([]);
  const apiFn = currentMainTab === "GHG Emissions" ? updateVarianceComparisonEmissions : currentMainTab === "Consumption" ? updateVarianceComparisonConsumption : updateVarianceComparisonFinancials;
  const { mutateAsync: updateVarianceComparison, isLoading } = useMutation(apiFn);
  const { total } = varianceComparisonData;
  const noDataAvailable = !isLoading && Object.values(varianceComparisonData).every(value =>
    typeof value === "object" && value !== null && Object.keys(value).length === 0
  );
  const isEmissionsChart = subTabName === "location" || subTabName === "market";

  const currentData: any[] = total ? Object.values(total).flat() : [];

  const totalCategories = currentData.map((entry: any) => entry.month + entry.year.replace("20", " '"));

  const currentPercentageVarianceAmount = currentData.map((entry: any) => isEmissionsChart ? entry[`${subTabName}BasedEmission`] : entry.consumption ?? entry.amount).map(amount => amount === null ? 0 : amount);
  const currentPercentageVariance = currentData.map((entry: any) => isEmissionsChart ? entry[`${subTabName}BasedEmissionVariance`] : entry.consumptionVariance ?? entry.amountVariance).map(variance => variance === null ? 0 : variance);

  const varianceOptions = [
    { label: "Same Period Last Month", value: 'mom' },
    { label: "Same Period Last Year", value: 'yoy' },
  ];

  const handleChangePeriod = (period: string) => {
    setComparisonPeriod(period);
  };

  const unitYAxis = isEmissionsChart ? "tCO2e" : currentData[0]?.unit ?? currentData[0]?.currency ?? "No Data Available";

  useEffect(() => {
    const handleUpdateComparisonPeriod = async () => {
      setVarianceComparisonData({});
      await updateVarianceComparison({
        ...searchFilters,
        compareFromDate: startDate.toISOString().substring(0, 10),
        compareToDate: endDate.toISOString().substring(0, 10),
        comparisonPeriod,
      }, {
        onSuccess: (response: any) => {
          setVarianceComparisonData(response.data);
        },
        onError: () => {
          // implement error handling
        },
      })
    };

    handleUpdateComparisonPeriod();
  }, [searchFilters, startDate, endDate, comparisonPeriod, updateVarianceComparison])

  const chartOptions = {
    chart: {
      spacing: [30, 50, 20, 40],
      width: null,
    },
    title: {
      text: `${chartTitle}${noDataAvailable ? ": No Data Available" : ''}`,
      align: "left",
      margin: 40,
      style: {
        ...commonStyles,
        color: COLORS.Mainblue,
        fontSize: "20px",
      },
    },
    xAxis: {
      categories: sortedDates(totalCategories),
      labels: {
        style: commonStyles,
      },
      startOnTick: false,
      endOnTick: false,
    },
    yAxis: [
      {
        title: {
          text: unitYAxis,
          style: commonStyles,
          offset: 50
        },
        labels: {
          style: commonStyles,
        },
        opposite: false,
      },
      {
        title: {
          text: `${unitYAxis} (%)`,
          style: commonStyles,
          offset: 50,
        },
        labels: {
          style: commonStyles,
          formatter() {
            return `${(this as any).value} %`;
          },
        },
        opposite: true,
        linkedTo: 0,
      },
    ],
    series: [
      {
        type: "column",
        name: "Variance %",
        data: currentPercentageVariance,
        color: chartColors[0],
        fillOpacity: 0.3,
        marker: { enabled: false },
      },
      {
        name: "Variance Amount",
        data: currentPercentageVarianceAmount.map(value => value * 1),
        color: chartColors[1],
        fillOpacity: 0.3,
        lineWidth: 2,
        marker: { enabled: true, symbol: "circle" },
      }
    ],
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            textOutline: "",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')} %</b>`;
          }
        },
      },
      line: {
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            textOutline: "",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
          },
          y: 100,
        },
      },
    },
    legend: {
      title: {
        style: commonStyles,
      },
      align: "left",
      verticalAlign: "bottom",
      x: 20,
      margin: 30,
    },
    tooltip: {
      enabled: false,
    },
    tickPositions: [-100, -50, 0, 50, 100],
    credits: {
      enabled: false,
    },
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 3,
      fontFamily: "Inter",
      position: "relative",
      "& > div:first-of-type": {
        borderRadius: 2,
      },
    }}>
      {isLoading && <Box sx={{ height: 300, position: "absolute", zIndex: 99999, right: "50%" }}>
        <Loading />
      </Box>}
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
      />
      <Box sx={{
        width: 240,
        position: "absolute",
        top: 22,
        right: 50,
      }}>
        <FormControl sx={{ width: "100%" }}>
          <Select
            displayEmpty
            sx={{ height: "36px", borderRadius: "8px", color: COLORS.Mainblue }}
            value={comparisonPeriod}
            onChange={(event) => handleChangePeriod(event.target.value)}
          >
            {varianceOptions.map(({ label, value }) => (
              <MenuItem key={label} value={value} sx={{ color: COLORS.Mainblue, fontFamily: "Inter", fontWeight: 500, fontSize: "14px" }}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};
